import { Box, IconButton, InputBase, Typography, ClickAwayListener, List, ListItemButton, ListItemText, Portal } from "@mui/material";
import { colors } from "../../../../theme/muiColors";
import SendIcon from "@mui/icons-material/Send";
import { useState, useRef, useEffect } from "react";
import { Contact } from "../../../internal-contacts/_types/internalContacts.types";
import { getContactTitle } from "../../../internal-contacts/_utils/internalContacts.utils";
import type { MessageGroupSummary } from "../../../message-groups/messageGroups.types";

interface SendNewInternalMessageProps {
  allContacts: Contact[];
  groups?: MessageGroupSummary[];
  onSelectContact: (contact: Contact) => void;
  onSelectGroup?: (group: MessageGroupSummary) => void;
}

export const SendNewInternalMessage = ({
  allContacts,
  groups = [],
  onSelectContact,
  onSelectGroup,
}: SendNewInternalMessageProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const filteredContacts = allContacts.filter((contact) => {
    if (!searchTerm) return false;

    const title = getContactTitle(contact).toLowerCase();
    const email = (contact.email || "").toLowerCase();
    const search = searchTerm.toLowerCase();
    return title.includes(search) || email.includes(search);
  });

  const filteredGroups = groups.filter((group) => {
    if (!searchTerm) return false;

    const search = searchTerm.toLowerCase();
    const groupText = [
      group.name,
      group.description,
      ...group.members.map((member) => `${member.firstName} ${member.lastName}`),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return groupText.includes(search);
  });

  const totalResults = filteredContacts.length + filteredGroups.length;

  useEffect(() => {
    if (open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [open, searchTerm]);

  const handleSelectContact = (contact: Contact) => {
    onSelectContact(contact);
    setSearchTerm("");
    setOpen(false);
  };

  const handleSelectGroup = (group: MessageGroupSummary) => {
    onSelectGroup?.(group);
    setSearchTerm("");
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && totalResults === 1) {
      e.preventDefault();
      if (filteredContacts.length === 1) {
        handleSelectContact(filteredContacts[0]);
      } else {
        handleSelectGroup(filteredGroups[0]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <Box>
          <Box
            ref={anchorRef}
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              gap: 1.5,
              px: 2,
              py: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: colors.blue[500],
                flexShrink: 0,
                lineHeight: 1,
              }}
            >
              To
            </Typography>

            <InputBase
              placeholder="Search internal contact..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={handleKeyDown}
              sx={{
                flex: 1,
                fontSize: 13,
                color: "var(--fx-surface-textsecondary, #64748B)",
                "& input": { p: 0 },
                "& input::placeholder": {
                  color: "var(--fx-surface-textsecondary, #64748B)",
                  opacity: 1,
                },
              }}
            />

            <IconButton
              type="button"
              size="small"
              disabled={totalResults !== 1}
              disableRipple
              onClick={() => {
                if (totalResults !== 1) return;
                if (filteredContacts.length === 1) {
                  handleSelectContact(filteredContacts[0]);
                } else {
                  handleSelectGroup(filteredGroups[0]);
                }
              }}
              sx={{
                p: 0.5,
                flexShrink: 0,
                transition: "opacity 0.15s",
                opacity: totalResults === 1 ? 1 : 0.25,
                color: colors.blue[500],
                "&:hover": {
                  bgcolor: "transparent",
                  color: colors.blue[300],
                },
                "&.Mui-disabled": {
                  color: colors.blue[300],
                },
              }}
            >
              <SendIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>

          {open && searchTerm.length > 0 && (
            <Portal>
              <Box
                sx={{
                  position: "fixed",
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  width: `${dropdownPosition.width}px`,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "8px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                  zIndex: 2147483647,
                  maxHeight: 240,
                  overflowY: "auto",
                  "&::-webkit-scrollbar": { width: 6 },
                  "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "divider",
                    borderRadius: "3px",
                  },
                }}
              >
                {totalResults > 0 ? (
                  <List disablePadding>
                    {filteredContacts.map((contact) => (
                      <ListItemButton
                        key={contact.uid}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelectContact(contact);
                        }}
                        sx={{
                          py: 1.5,
                          "&:hover": {
                            bgcolor: colors.blue[50],
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: "text.primary",
                              }}
                            >
                              {getContactTitle(contact)}
                            </Typography>
                          }
                          secondary={
                            contact.email ? (
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  color: "text.secondary",
                                }}
                              >
                                {contact.email}
                              </Typography>
                            ) : undefined
                          }
                        />
                      </ListItemButton>
                    ))}
                    {filteredGroups.map((group) => (
                      <ListItemButton
                        key={`group-${group.groupUid}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelectGroup(group);
                        }}
                        sx={{
                          py: 1.5,
                          "&:hover": {
                            bgcolor: colors.blue[50],
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: "text.primary",
                              }}
                            >
                              {group.name}
                            </Typography>
                          }
                          secondary={
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: "text.secondary",
                              }}
                            >
                              Group · {group.membersCount} {group.membersCount === 1 ? "member" : "members"}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ px: 2, py: 2, textAlign: "center" }}>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                      No contacts or groups found for "{searchTerm}"
                    </Typography>
                  </Box>
                )}
              </Box>
            </Portal>
          )}
        </Box>
      </ClickAwayListener>
    </Box>
  );
};
